import { FormProvider, useForm } from "react-hook-form"
import { graphql, useFragment, useLazyLoadQuery } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { DialogDescription } from "@/components/ui/dialog"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { DateOnly } from "@/lib/date-only"
import { ChEditBillingSection } from "@/modules/common/billing/components/billing-form/ch/edit/ch-edit-billing-section"
import { getChEditBillingDefaultValues } from "@/modules/common/billing/components/billing-form/ch/edit/libs/getChEditBillingDefaultValues"
import {
  ChEditBillingSchemaType,
  createChEditBillingSchema,
} from "@/modules/common/billing/components/billing-form/ch/edit/schema/ch-edit-billing-schema"
import { EMPLOYER_INSURANCE } from "@/modules/common/billing/components/billing-form/constants"
import { BillingDialogFooter } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/billing/billing-dialog-footer"
import { chBillingDialogContentBillingFragment$key } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/billing/ch/__generated__/chBillingDialogContentBillingFragment.graphql"
import { chBillingDialogContentBillingQuery } from "@/modules/patients/agenda/teleconsultation/appointment-action-menu/billing/ch/__generated__/chBillingDialogContentBillingQuery.graphql"
import { EditBillingAttributesInput } from "@/modules/patients/detail/conclude/billing-section/services/__generated__/useEditBillingAttributesMutation.graphql"
import { useEditBillingDialogMutation } from "@/modules/patients/detail/conclude/billing-section/services/useEditBillingAttributesMutation"

export const chBillingDialogContentBillingQueryDescriptor = graphql`
  query chBillingDialogContentBillingQuery($id: ID!) {
    appointmentById(id: $id) {
      ...chBillingDialogContentBillingFragment
    }
  }
`

const chBillingDialogContentBillingFragment = graphql`
  fragment chBillingDialogContentBillingFragment on Appointment {
    id
    service
    billingAttribute {
      id
      isNotBillable
      notBillableReason
      treatmentReason
      reason
      currentGestationWeek
      organisationId
      dateOfAccident
      accidentReference
      isEmployer
      lawType
      ivgCaseReference
      mvgCaseReference
    }
  }
`

type ChBillingDialogContentProps = {
  isEditable: boolean
  appointmentId: string
  onCancel: () => void
  onSave: () => void
}

export const ChBillingDialogContent = ({
  isEditable,
  appointmentId,
  onCancel,
  onSave,
}: ChBillingDialogContentProps) => {
  const data = useLazyLoadQuery<chBillingDialogContentBillingQuery>(chBillingDialogContentBillingQueryDescriptor, {
    id: appointmentId,
  })

  const appointment = useFragment<chBillingDialogContentBillingFragment$key>(
    chBillingDialogContentBillingFragment,
    data.appointmentById,
  )

  if (!appointment) {
    throw new Error("Appointment not found")
  }

  const billingData = appointment.billingAttribute
  const isAdministrativeRequest = appointment.service === "ADMINISTRATIVE_REQUEST"

  const form = useForm<ChEditBillingSchemaType>({
    mode: "all",
    resolver: zodResolver(createChEditBillingSchema()),
    defaultValues: getChEditBillingDefaultValues(appointment, billingData),
  })

  const { handleSubmit } = form

  const [commitBillingDialogMutation, isFlight] = useEditBillingDialogMutation()

  const handleSave = async (formData: ChEditBillingSchemaType) => {
    const {
      isNotBillable,
      reason,
      notBillableReason,
      treatmentReason,
      dateOfAccident,
      organisationId,
      lawType,
      ivgCaseReference,
      mvgCaseReference,
      accidentReference,
    } = formData

    const appointmentId = appointment.id

    let editBillingInput: EditBillingAttributesInput = { appointmentId, isNotBillable }

    if (isNotBillable) {
      editBillingInput = {
        ...editBillingInput,
        reason: notBillableReason === "OTHER_REASON" ? reason : null,
        notBillableReason,
      }
    } else {
      const isEmployerInsurance = organisationId === EMPLOYER_INSURANCE

      const uvgFields =
        lawType === "UVG"
          ? {
              dateOfAccident: dateOfAccident ? new DateOnly(dateOfAccident).toString() : null,
              organisationId:
                organisationId && organisationId.length > 0 && !isEmployerInsurance ? organisationId : null,
              // BE requiring either of isEmployer or organisationId must be null
              isEmployer: isEmployerInsurance || null,
              accidentReference: accidentReference || null,
            }
          : {}

      const ivgFields = lawType === "IVG" ? { ivgCaseReference: ivgCaseReference || null } : {}
      const mvgFields = lawType === "MVG" ? { mvgCaseReference: mvgCaseReference || null } : {}

      editBillingInput = {
        ...editBillingInput,
        treatmentReason,
        lawType,
        ...uvgFields,
        ...ivgFields,
        ...mvgFields,
      }
    }

    commitBillingDialogMutation(editBillingInput, () => {
      onSave()
    })
    FaroInsightsWrapper.pushEvent("agenda_billing_information_save")
  }

  return (
    <FormProvider {...form}>
      <DialogDescription>
        <ChEditBillingSection disabled={!isEditable} isAdministrativeRequest={isAdministrativeRequest} />
      </DialogDescription>
      <BillingDialogFooter
        isEditable={isEditable}
        isLoading={isFlight}
        onSave={handleSubmit(handleSave)}
        onCancel={onCancel}
      />
    </FormProvider>
  )
}
